"use client";

interface CookieOptions {
  expires?: Date | number;
  maxAge?: number;
  domain?: string;
  path?: string;
  secure?: boolean;
  sameSite?: "strict" | "lax" | "none";
  httpOnly?: boolean;
  encode?: boolean;
}

interface CookieDeleteOptions {
  path?: string;
  domain?: string;
}

interface CookieToDelete {
  name: string;
  options?: CookieDeleteOptions;
}

interface CookieToSet {
  name: string;
  value: string | boolean | number;
  options?: CookieOptions;
}

export function setDocumentCookie(
  cookies:
    | CookieToSet
    | CookieToSet[]
    | { name: string; value: string; options?: CookieOptions }
): void {
  if (typeof document === "undefined") return;

  const cookiesToSet = Array.isArray(cookies) ? cookies : [cookies];

  cookiesToSet.forEach(({ name, value, options = {} }) => {
    let cookieString = `${name}=${value}`;

    if (options.expires) {
      const expires =
        options.expires instanceof Date
          ? options.expires
          : new Date(Date.now() + options.expires * 24 * 60 * 60 * 1000);
      cookieString += `; expires=${expires.toUTCString()}`;
    }

    if (options.maxAge) cookieString += `; max-age=${options.maxAge}`;
    if (options.domain) cookieString += `; domain=${options.domain}`;
    cookieString += `; path=${options.path || "/"}`;
    const isSecure =
      options.secure ??
      (typeof window !== "undefined" && window.location.protocol === "https:");
    if (isSecure) cookieString += "; Secure=true";
    cookieString += `; samesite=${options.sameSite || "lax"}`;
    if (options.httpOnly === true) cookieString += "; HttpOnly";
    document.cookie = cookieString;
  });
}

export function getCookie(name: string): string | null {
  if (typeof document === "undefined") {
    return null;
  }
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(";").shift() || null;
  }
  return null;
}

export function deleteDocumentCookie(
  cookies:
    | CookieToDelete
    | CookieToDelete[]
    | { name: string; options?: CookieDeleteOptions }
): void {
  if (typeof document === "undefined") return;

  const cookiesToDelete = Array.isArray(cookies) ? cookies : [cookies];

  cookiesToDelete.forEach(({ name, options = {} }) => {
    const path = options.path || "/";
    let cookieString = `${name}=; max-age=0; path=${path};`;
    if (options.domain) cookieString += ` domain=${options.domain};`;
    document.cookie = cookieString;
  });
}
